import { useState, useEffect, useCallback } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Camera, Check, X, Loader2, AlertCircle } from "lucide-react";

export default function PhotoReviewPanel() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState({});
  const [rejectReasons, setRejectReasons] = useState({});
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/photo-review-queue`);
      setQueue(res.data?.queue || []);
      setLastRefreshedAt(Date.now());
    } catch { /* */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleAction = async (item, action) => {
    const key = `${item.business_id}-${item.photo_type}`;
    setActioning(p => ({ ...p, [key]: true }));
    try {
      const reason = action === "needs_retake" ? (rejectReasons[key] || "Photo does not meet quality standards") : "";
      await axios.post(`${API}/admin/photo-review/${item.photo_type}/${item.business_id}`, {
        action,
        reason,
      });
      toast.success(`Photo ${action === "verify" ? "verified" : "rejected"}`);
      setQueue(q => q.filter(i => !(i.business_id === item.business_id && i.photo_type === item.photo_type)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setActioning(p => ({ ...p, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="rounded-xl bg-white border border-slate-200 p-6 text-center" data-testid="photo-review-empty">
        <Camera className="w-6 h-6 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">No photos pending review</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="photo-review-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetchQueue} lastRefreshedAt={lastRefreshedAt} testId="photo-review-refresh" /></div>
      <div className="flex items-center gap-2 mb-2">
        <Camera className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-bold text-slate-800">Photo Review Queue</h3>
        <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold" data-testid="photo-review-count">{queue.length}</span>
      </div>

      {queue.map(item => {
        const key = `${item.business_id}-${item.photo_type}`;
        return (
          <div key={key} className="rounded-xl bg-white border border-slate-200 p-4" data-testid={`review-item-${key}`}>
            <div className="flex items-start gap-3">
              <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                <Camera className="w-6 h-6 text-slate-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">{item.truck_name || item.business_id}</p>
                <p className="text-[10px] text-slate-400">
                  {item.photo_type === "truck_photo" ? "Truck Exterior" : "Menu Board"}
                  {item.uploaded_at && ` — ${new Date(item.uploaded_at).toLocaleDateString()}`}
                </p>
                {item.reason && (
                  <p className="text-[10px] text-amber-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {item.reason}
                  </p>
                )}

                {/* Reject reason input */}
                <input
                  type="text"
                  value={rejectReasons[key] || ""}
                  onChange={(e) => setRejectReasons(p => ({ ...p, [key]: e.target.value }))}
                  placeholder="Rejection reason (optional)"
                  className="w-full mt-2 text-[11px] px-2 py-1.5 rounded border border-slate-200 outline-none"
                  data-testid={`reject-reason-${key}`}
                />

                {/* Actions */}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => handleAction(item, "verify")}
                    disabled={actioning[key]}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-[11px] font-bold border border-emerald-200 hover:bg-emerald-100 disabled:opacity-50"
                    data-testid={`verify-btn-${key}`}
                  >
                    {actioning[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Verify
                  </button>
                  <button
                    onClick={() => handleAction(item, "needs_retake")}
                    disabled={actioning[key]}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-[11px] font-bold border border-red-200 hover:bg-red-100 disabled:opacity-50"
                    data-testid={`reject-btn-${key}`}
                  >
                    {actioning[key] ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                    Needs Retake
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
